/**
 * White Paper tab — generate, view (rendered markdown), and download
 * research papers built from the strongest cached findings.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { Download, FileText, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { API, apiGet, apiPost } from "./statsApi";

export const WhitePaperTab = ({ token }) => {
  const [paper, setPaper] = useState(null);
  const [list, setList] = useState([]);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [latest, lst] = await Promise.all([
        apiGet("/stats/whitepaper/latest", token),
        apiGet("/stats/whitepaper/list", token),
      ]);
      setPaper(latest.status === "not_generated" ? null : latest);
      setList(lst.whitepapers || []);
    } catch (e) {
      console.error(e);
    }
  }, [token]);

  const poll = useCallback(async () => {
    const st = await apiGet("/stats/whitepaper/status", token).catch(() => null);
    setJob(st);
    if (st?.status === "running") {
      setRunning(true);
      return false;
    }
    setRunning(false);
    return st;
  }, [token]);

  useEffect(() => {
    if (!token) return;
    load();
    (async () => {
      const st = await poll();
      if (st === false) startPolling();
    })();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const st = await poll();
      if (st !== false) {
        clearInterval(pollRef.current);
        if (st?.status === "completed") {
          toast.success("White paper generated");
          load();
        } else if (st?.status === "failed") {
          toast.error(`Generation failed: ${st.error || "unknown"}`);
        }
      }
    }, 3000);
  }, [poll, load]);

  const generate = async () => {
    try {
      await apiPost("/stats/whitepaper/generate", token);
      setRunning(true);
      setJob({ status: "running", progress: 0, message: "Starting..." });
      toast.info("White paper generation started");
      startPolling();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const download = () => {
    if (!paper) return;
    const blob = new Blob([paper.markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kensys-signal-whitepaper-${(paper.generated_at || "").slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const viewPaper = async (id) => {
    try {
      const doc = await apiGet(`/stats/whitepaper/${id}`, token);
      setPaper(doc);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deletePaper = async (id) => {
    try {
      await fetch(`${API}/stats/whitepaper/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="whitepaper-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle>Research White Paper</CardTitle>
              <CardDescription>
                Auto-drafted from the statistically strongest cached findings (all numbers are
                computed locally — the writer cannot invent statistics). Run the signal analyses
                first, then generate.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {paper && (
                <Button variant="outline" size="sm" onClick={download} data-testid="whitepaper-download-btn">
                  <Download className="w-4 h-4 mr-2" />Download .md
                </Button>
              )}
              <Button size="sm" onClick={generate} disabled={running} data-testid="whitepaper-generate-btn">
                {running ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />{paper ? "Regenerate" : "Generate White Paper"}</>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {running && job && (
          <CardContent>
            <Progress value={job.progress || 0} className="h-2 mb-1" />
            <div className="text-xs text-muted-foreground">{job.message}</div>
          </CardContent>
        )}
      </Card>

      {list.length > 1 && (
        <div className="flex gap-2 flex-wrap items-center text-xs" data-testid="whitepaper-versions">
          <span className="text-muted-foreground">Versions:</span>
          {list.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1">
              <Badge
                variant={paper?.id === p.id ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => viewPaper(p.id)}
              >
                {(p.generated_at || "").slice(0, 16).replace("T", " ")}
              </Badge>
              <Trash2
                className="w-3 h-3 text-muted-foreground hover:text-destructive cursor-pointer"
                onClick={() => deletePaper(p.id)}
              />
            </span>
          ))}
        </div>
      )}

      {paper ? (
        <Card data-testid="whitepaper-content">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
              <FileText className="w-4 h-4" />
              Generated {new Date(paper.generated_at).toLocaleString()} · model {paper.model} ·
              signals: {(paper.signals_included || []).join(", ")}
            </div>
            <article className="prose prose-invert prose-sm max-w-none
              prose-headings:font-semibold prose-table:text-xs
              prose-th:border prose-th:border-border prose-th:px-2 prose-th:py-1
              prose-td:border prose-td:border-border prose-td:px-2 prose-td:py-1">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{paper.markdown}</ReactMarkdown>
            </article>
          </CardContent>
        </Card>
      ) : (
        !running && (
          <div className="text-center py-16 text-muted-foreground" data-testid="whitepaper-empty">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No white paper generated yet.</p>
            <p className="text-sm mt-1">Run at least one signal analysis, then click Generate.</p>
          </div>
        )
      )}
    </div>
  );
};
